import React, { useCallback, useEffect } from "react";
import { css } from "glamor";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import { getCurrentCourseUser } from "../../selectors/coursesUserSelectors";
import { getCurrentCourseId } from "../../selectors/courseSelectors";
import { selectStudyGoalByProps } from "../../selectors/studyGoalSelectors";
import {
  selectStudyPlanDays,
  selectStudyPlanEnrichedDays,
  selectStudyPlanCompletedLessons,
  selectStudyPlanTruncated,
} from "../../selectors/studyPlanSelectors";
import { computeStudyPlan } from "../../processes/studyPlanProcesses";
import StudyPlanContinueCard from "./StudyPlanContinueCard";
import StudyPlanLessonRow from "./StudyPlanLessonRow";
import { colors, spacing } from "../library/theme";
import { uiColors } from "../library/theme";

// ── two-column layout ────────────────────────────────────────────────────────

const layoutClass = css({
  display: "flex",
  gap: spacing.l,
  alignItems: "flex-start",
  "@media (max-width: 900px)": {
    flexDirection: "column",
  },
});

const mainColumnClass = css({
  flex: 1,
  minWidth: 0,
});

const sideColumnClass = css({
  width: 300,
  flexShrink: 0,
  position: "sticky",
  top: spacing.m,
  "@media (max-width: 900px)": {
    width: "100%",
    position: "static",
  },
});

// ── watched lessons card ─────────────────────────────────────────────────────

const watchedSectionClass = css({
  border: `1px solid ${colors.greyLight}`,
  borderRadius: 12,
  boxShadow: `0px 2px 4px ${uiColors.border}`,
  overflow: "hidden",
});

const watchedHeaderClass = css({
  padding: `${spacing.m} ${spacing.m}`,
});

const watchedTitleStyle = {
  fontSize: 16,
  fontWeight: 700,
  color: colors.greyShadow,
  margin: 0,
};

const watchedCountStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: colors.greyDark,
  marginTop: 2,
};

const watchedListClass = css({
  padding: `0 ${spacing.s} ${spacing.m}`,
  display: "flex",
  flexDirection: "column",
  gap: 4,
  maxHeight: "70vh",
  overflowY: "auto",
});

const moreCountStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: colors.greyDark,
  textAlign: "center",
  paddingTop: spacing.xs,
};

const truncatedBannerClass = css({
  padding: `${spacing.m} ${spacing.l}`,
  backgroundColor: "#FFF3E0",
  border: "1px solid #FFE082",
  borderRadius: 12,
  textAlign: "center",
  fontSize: 14,
  color: "#E65100",
  fontWeight: 500,
  marginTop: spacing.m,
});

function CourseDailyStudyPlanContainer({
  dispatch,
  studyGoal,
  lessonEntities,
  levels,
  lessonsUsers,
  courseId,
  userId,
  days,
  enrichedDays,
  completedLessons,
  truncated,
}) {
  useEffect(() => {
    computeStudyPlan(dispatch, {
      lessons: lessonEntities,
      levels,
      lessonsUsers,
      studyGoal,
      courseId,
      userId,
    });
  }, [dispatch, lessonEntities, levels, lessonsUsers, studyGoal, courseId, userId]);

  const handlePlayLesson = useCallback(
    (lesson) => {
      const cId = lesson.courseId || courseId;
      if (cId && lesson.id) {
        dispatch(push(`/courses/${cId}/lessons/${lesson.id}?source=study-plan`));
      }
    },
    [dispatch, courseId]
  );

  if (!studyGoal?.id) {
    return null;
  }

  if (!days || days.length === 0) {
    return null;
  }

  const todayDay = days.find((d) => d.isToday);
  const todayCompletedIds = new Set(
    (todayDay?.lessons || [])
      .filter((l) => l.type === "lesson" && l.status === "completed")
      .map((l) => l.id)
  );
  const watchedLessons = (completedLessons || []).filter(
    (l) => !todayCompletedIds.has(l.id)
  );

  return (
    <div className={layoutClass}>
      <div className={mainColumnClass}>
        {days.map((day, index) => (
          <StudyPlanContinueCard
            key={index}
            day={day}
            items={enrichedDays[index] || []}
            onPlayLesson={handlePlayLesson}
          />
        ))}
        {truncated && (
          <div className={truncatedBannerClass}>
            O plano de estudos mostra apenas os próximos 120 dias.
            Conforme você avançar, novos dias serão exibidos.
          </div>
        )}
      </div>

      {watchedLessons.length > 0 && (
        <div className={sideColumnClass}>
          <div className={watchedSectionClass}>
            <div className={watchedHeaderClass}>
              <h3 style={watchedTitleStyle}>Aulas assistidas</h3>
              <span style={watchedCountStyle}>
                {watchedLessons.length}{" "}
                {watchedLessons.length === 1 ? "aula" : "aulas"}
              </span>
            </div>

            <div className={watchedListClass}>
              {watchedLessons.slice(-10).map((lesson) => (
                <StudyPlanLessonRow
                  key={lesson.id}
                  lesson={lesson}
                  visualStatus="completed"
                  onPlay={handlePlayLesson}
                  isToday={false}
                  overBudget={false}
                  showReviewButton={false}
                />
              ))}
              {watchedLessons.length > 10 && (
                <span style={moreCountStyle}>
                  +{watchedLessons.length - 10} aulas
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function mapStateToProps(state) {
  const coursesUser = getCurrentCourseUser(state);
  return {
    courseId: getCurrentCourseId(state),
    studyGoal: selectStudyGoalByProps(state, {
      coursesUserId: coursesUser?.id,
    }),
    lessonEntities: state.entities.lessons,
    levels: state.entities.levels,
    lessonsUsers: state.entities.lessonsUsers,
    userId: state.currentUserId,
    days: selectStudyPlanDays(state),
    enrichedDays: selectStudyPlanEnrichedDays(state),
    completedLessons: selectStudyPlanCompletedLessons(state),
    truncated: selectStudyPlanTruncated(state),
  };
}

export default connect(mapStateToProps)(CourseDailyStudyPlanContainer);
